import React from 'react';

/**
 * DataQualityDisplay - Shows data quality metrics for Budget Agent
 *
 * Displays transaction tagging percentages from NetSuite scan
 */
const DataQualityDisplay = ({ dataQuality, onConfirm, onClose }) => {
  const {
    total_transactions = 0,
    subsidiary_tagged = 0,
    department_tagged = 0,
    class_tagged = 0,
    location_tagged = 0,
    subsidiary_count = 0
  } = dataQuality || {};

  // Helper to get color based on percentage
  const getPercentageColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600 dark:text-green-400';
    if (percentage >= 50) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  // Helper to get progress bar color
  const getProgressBarColor = (percentage) => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000] backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl w-[98%] max-w-[1800px] max-h-[90vh] flex flex-col shadow-2xl overflow-hidden p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-white flex items-center gap-2">
          <span>📊</span> Data Quality Analysis
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Review tagging quality before categorization
        </p>
      </div>

      {/* Total Transactions & Subsidiary Count */}
      <div className="mb-6 grid grid-cols-2 gap-4">
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <div className="text-sm text-gray-600 dark:text-gray-400">Total Transactions Analyzed</div>
          <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
            {total_transactions.toLocaleString()}
          </div>
        </div>
        <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
          <div className="text-sm text-gray-600 dark:text-gray-400">Subsidiaries Selected</div>
          <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
            {subsidiary_count > 0 ? subsidiary_count : 'All'}
          </div>
        </div>
      </div>

      {/* Quality Metrics */}
      <div className="space-y-4">
        {/* Subsidiary Tagged */}
        <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Subsidiary Tagged
            </span>
            <span className={`text-lg font-semibold ${getPercentageColor(subsidiary_tagged)}`}>
              {subsidiary_tagged}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${getProgressBarColor(subsidiary_tagged)}`}
              style={{ width: `${subsidiary_tagged}%` }}
            />
          </div>
        </div>

        {/* Department Tagged */}
        <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Department Tagged
            </span>
            <span className={`text-lg font-semibold ${getPercentageColor(department_tagged)}`}>
              {department_tagged}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${getProgressBarColor(department_tagged)}`}
              style={{ width: `${department_tagged}%` }}
            />
          </div>
        </div>

        {/* Class Tagged */}
        <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Class Tagged
            </span>
            <span className={`text-lg font-semibold ${getPercentageColor(class_tagged)}`}>
              {class_tagged}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${getProgressBarColor(class_tagged)}`}
              style={{ width: `${class_tagged}%` }}
            />
          </div>
        </div>

        {/* Location Tagged */}
        <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Location Tagged
            </span>
            <span className={`text-lg font-semibold ${getPercentageColor(location_tagged)}`}>
              {location_tagged}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${getProgressBarColor(location_tagged)}`}
              style={{ width: `${location_tagged}%` }}
            />
          </div>
        </div>
      </div>

      {/* Action Button */}
      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={() => onConfirm && onConfirm({ action: 'submit' })}
          className="w-full py-3 px-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all shadow-md"
        >
          Categorize Accounts
        </button>
      </div>
    </div>
    </div>
  );
};

export default DataQualityDisplay;
